import React, { ReactNode } from "react";
import { useImmer } from "use-immer";

import { IgButton, IgCloseButton } from "@intelligems/ig-theme";
import { GetProcessesResponseBodyType } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { useGetOrg } from "../../../api/organizations/get-organization";
import { useDeleteProcess } from "../../../api/processes/delete-process";
import { useGetProcesses } from "../../../api/processes/get-processes";
import { processesKeys } from "../../../api/queryKeys";
import { IgAlert } from "../../Utils/IgAlert";

export const Processes = () => {
  const [isLoading, setIsLoading] = useImmer<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const organization = useGetOrg();
  const processes = useGetProcesses();
  const deleteProcess = useDeleteProcess();

  const handleDelete = (processId: string) => {
    setIsLoading((draft) => {
      draft[processId] = true;
      return draft;
    });

    queryClient.setQueryData<GetProcessesResponseBodyType>(
      processesKeys.one(organization.data.org.id),
      (data) => {
        return {
          processes: data?.processes.filter((p) => p.id !== processId) || [],
        };
      }
    );

    deleteProcess.mutate(
      {
        body: undefined,
        pathParams: { processId: processId },
        queryParams: undefined,
      },
      {
        onSettled: () => {
          setIsLoading((draft) => {
            draft[processId] = false;
          });
        },
      }
    );
  };

  if (!processes.isSuccess || !processes.data.processes.length) {
    return null;
  }

  return (
    <Grid
      container
      direction={"column"}
      sx={{ marginBottom: "20px" }}
      rowSpacing={1}
    >
      {processes.data.processes.map((process) => {
        let buttons: ReactNode[] = [];
        if (process.action) {
          buttons = [
            <Grid
              item
              key={"dismiss-alert"}
              sx={{ marginTop: "auto", marginBottom: "auto" }}
            >
              <IgButton
                igVariant={
                  process.severity === "info" ? undefined : process.severity
                }
                variant={"contained"}
                onClick={() => {
                  if (process.action?.href) {
                    window.open(process.action?.href, "_blank");
                  } else {
                    handleDelete(process.id);
                  }
                }}
              >
                {process.actionMessage || "OK"}
              </IgButton>
            </Grid>,
          ];
        }

        return (
          <Grid container item key={process.id} sx={{ zIndex: 1301 }}>
            <IgAlert
              severity={process.severity}
              title={process.title}
              message={process.message}
              loading={(!process.action && process.isLoading) || false}
              loadingPercent={process.loadingPercent || undefined}
              longMessage={process.longMessage}
              buttons={[
                ...buttons,
                <Grid
                  item
                  key={"dismiss-alert"}
                  sx={{ marginTop: "auto", marginBottom: "auto" }}
                >
                  <IgCloseButton
                    disabled={isLoading[process.id] || false}
                    onClick={() => handleDelete(process.id)}
                  />
                </Grid>,
              ]}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};
